import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { LogOut, Save, Plus, Trash2, Loader2, ShieldCheck } from "lucide-react";

const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_fanta-imperyum/artifacts/2bu03nrd_Logo%20con%20foglie%20di%20alloro%20dorate.png";

const Field = ({ label, value, onChange, textarea, type = "text", testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">{label}</span>
    {textarea ? (
      <textarea
        data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={4}
        className="mt-2 w-full rounded-lg bg-black/50 border border-white/15 px-3 py-2.5 text-white outline-none focus:border-imperygreen transition-colors"
      />
    ) : (
      <input
        data-testid={testid} type={type} value={value}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        className="mt-2 w-full rounded-lg bg-black/50 border border-white/15 px-3 py-2.5 text-white outline-none focus:border-imperygreen transition-colors"
      />
    )}
  </label>
);

const Card = ({ title, children }) => (
  <div className="rounded-2xl glass gold-border p-5 md:p-6">
    <h3 className="font-heading text-lg font-bold text-gold mb-4">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

function Login({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("fi_token", data.token);
      onAuth(data.user);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-void px-5 grain">
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-2xl glass gold-border p-8" data-testid="login-form">
        <img src={LOGO} alt="FANTA IMPERYUM" className="h-12 mx-auto" />
        <h1 className="mt-6 text-center font-heading text-2xl font-black text-gold-shine">AREA IMPERATORE</h1>
        <p className="text-center text-sm text-white/50 mt-1">Accedi per gestire i contenuti</p>
        <div className="mt-6 space-y-4">
          <Field label="Email" value={email} onChange={setEmail} testid="login-email" />
          <Field label="Password" value={password} onChange={setPassword} type="password" testid="login-password" />
        </div>
        {err && <p className="mt-4 text-sm text-red-400" data-testid="login-error">{err}</p>}
        <button
          type="submit" disabled={loading} data-testid="login-submit"
          className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-imperygreen py-3 font-heading font-bold text-black hover:shadow-[0_0_24px_rgba(0,255,102,0.5)] transition-shadow disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />} ACCEDI
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setC(r.data)); }, []);

  const set = (path, value) => {
    setC((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { data: c });
      toast.success("Contenuti salvati con successo");
    } catch (e) {
      if (e.response?.status === 401) { onLogout(); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!c) return <div className="min-h-screen flex items-center justify-center bg-void"><Loader2 className="animate-spin text-imperygreen" /></div>;

  return (
    <div className="min-h-screen bg-void text-white pb-28">
      <Toaster theme="dark" position="top-center" />
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="mx-auto max-w-5xl px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="" className="h-8" />
            <span className="font-heading font-bold text-sm text-gold hidden sm:block">CMS</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} data-testid="save-btn" className="flex items-center gap-2 rounded-full bg-imperygreen px-5 py-2.5 text-sm font-bold text-black hover:shadow-[0_0_20px_rgba(0,255,102,0.5)] transition-shadow disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Salva
            </button>
            <button onClick={onLogout} data-testid="logout-btn" className="flex items-center gap-2 rounded-full gold-border px-4 py-2.5 text-sm text-gold hover:bg-gold/10 transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8 grid gap-5">
        <Card title="Hero">
          <Field label="Titolo" value={c.hero.title} onChange={(v) => set(["hero", "title"], v)} testid="admin-hero-title" />
          <Field label="Sottotitolo" value={c.hero.subtitle} onChange={(v) => set(["hero", "subtitle"], v)} />
          <Field label="Tagline" value={c.hero.tagline} onChange={(v) => set(["hero", "tagline"], v)} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="CTA principale" value={c.hero.cta_primary} onChange={(v) => set(["hero", "cta_primary"], v)} />
            <Field label="CTA secondaria" value={c.hero.cta_secondary} onChange={(v) => set(["hero", "cta_secondary"], v)} />
          </div>
        </Card>

        <Card title="Link della lega">
          <Field label="Link / codice lega (usato da tutte le CTA)" value={c.league_link} onChange={(v) => set(["league_link"], v)} testid="admin-league-link" />
        </Card>

        <Card title="Cos'e FANTA IMPERYUM">
          <Field label="Titolo" value={c.about.title} onChange={(v) => set(["about", "title"], v)} />
          <Field label="Testo" value={c.about.text} onChange={(v) => set(["about", "text"], v)} textarea />
        </Card>

        <Card title="Come entrare — Step">
          {c.steps.map((s, i) => (
            <div key={i} className="rounded-lg border border-white/10 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Numero" value={s.num} onChange={(v) => set(["steps", i, "num"], v)} />
                <Field label="Titolo" value={s.title} onChange={(v) => set(["steps", i, "title"], v)} />
              </div>
              <Field label="Testo" value={s.text} onChange={(v) => set(["steps", i, "text"], v)} textarea />
            </div>
          ))}
        </Card>

        <Card title="Road Map">
          <Field label="Sottotitolo" value={c.roadmap.subtitle} onChange={(v) => set(["roadmap", "subtitle"], v)} />
          <Field label="Partecipanti attuali" value={c.roadmap.current_participants} onChange={(v) => set(["roadmap", "current_participants"], v)} type="number" testid="admin-current-participants" />
          <div className="space-y-3">
            {c.roadmap.milestones.map((m, i) => (
              <div key={i} className="flex items-end gap-3 rounded-lg border border-white/10 p-3">
                <div className="w-28"><Field label="N. Part." value={m.participants} onChange={(v) => set(["roadmap", "milestones", i, "participants"], v)} type="number" /></div>
                <div className="flex-1"><Field label="Premio" value={m.prize} onChange={(v) => set(["roadmap", "milestones", i, "prize"], v)} /></div>
                <button onClick={() => set(["roadmap", "milestones"], c.roadmap.milestones.filter((_, j) => j !== i))} className="rounded-lg bg-red-500/15 p-2.5 text-red-400 hover:bg-red-500/25 transition-colors"><Trash2 size={16} /></button>
              </div>
            ))}
            <button onClick={() => set(["roadmap", "milestones"], [...c.roadmap.milestones, { participants: 0, prize: "[PREMIO]" }])} className="flex items-center gap-2 text-sm text-imperygreen"><Plus size={16} /> Aggiungi traguardo</button>
          </div>
        </Card>

        <Card title="Regolamento">
          <Field label="Titolo" value={c.regolamento?.title || ""} onChange={(v) => set(["regolamento", "title"], v)} />
          <Field label="Testo introduttivo" value={c.regolamento?.text || ""} onChange={(v) => set(["regolamento", "text"], v)} textarea />
          <div className="space-y-3">
            {(c.regolamento?.rules || []).map((r, i) => (
              <div key={i} className="flex items-end gap-3 rounded-lg border border-white/10 p-3">
                <div className="flex-1"><Field label={`Regola ${i + 1}`} value={r} onChange={(v) => set(["regolamento", "rules", i], v)} /></div>
                <button onClick={() => set(["regolamento", "rules"], c.regolamento.rules.filter((_, j) => j !== i))} className="rounded-lg bg-red-500/15 p-2.5 text-red-400 hover:bg-red-500/25 transition-colors"><Trash2 size={16} /></button>
              </div>
            ))}
            <button onClick={() => set(["regolamento", "rules"], [...(c.regolamento?.rules || []), "Nuova regola"])} className="flex items-center gap-2 text-sm text-imperygreen"><Plus size={16} /> Aggiungi regola</button>
          </div>
        </Card>

        <Card title="Premi (per traguardo)">
          <Field label="Titolo sezione" value={c.prizes.title} onChange={(v) => set(["prizes", "title"], v)} />
          {(c.prizes.tiers || []).map((tier, ti) => (
            <div key={ti} className="rounded-xl border border-gold/25 p-4 space-y-3">
              <div className="flex items-end justify-between gap-3">
                <div className="w-48"><Field label="Partecipanti traguardo" value={tier.participants} onChange={(v) => set(["prizes", "tiers", ti, "participants"], v)} type="number" /></div>
                <button onClick={() => set(["prizes", "tiers"], c.prizes.tiers.filter((_, j) => j !== ti))} className="rounded-lg bg-red-500/15 px-3 py-2.5 text-xs text-red-400 hover:bg-red-500/25 transition-colors">Rimuovi traguardo</button>
              </div>
              {(tier.items || []).map((p, i) => (
                <div key={i} className="rounded-lg border border-white/10 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/40">Premio #{i + 1}{i === 0 ? " (in evidenza)" : ""}</span>
                    <button onClick={() => set(["prizes", "tiers", ti, "items"], tier.items.filter((_, j) => j !== i))} className="rounded-lg bg-red-500/15 p-2 text-red-400 hover:bg-red-500/25 transition-colors"><Trash2 size={15} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Posizione" value={p.position} onChange={(v) => set(["prizes", "tiers", ti, "items", i, "position"], v)} />
                    <Field label="Valore" value={p.value} onChange={(v) => set(["prizes", "tiers", ti, "items", i, "value"], v)} />
                  </div>
                  <Field label="Nome premio" value={p.name} onChange={(v) => set(["prizes", "tiers", ti, "items", i, "name"], v)} />
                  <Field label="Condizione (opzionale)" value={p.condition} onChange={(v) => set(["prizes", "tiers", ti, "items", i, "condition"], v)} />
                </div>
              ))}
              <button onClick={() => set(["prizes", "tiers", ti, "items"], [...(tier.items || []), { position: "", name: "[PREMIO]", value: "EUR XXX", condition: "" }])} className="flex items-center gap-2 text-sm text-imperygreen"><Plus size={16} /> Aggiungi premio</button>
            </div>
          ))}
          <button onClick={() => set(["prizes", "tiers"], [...(c.prizes.tiers || []), { participants: 0, items: [{ position: "1 CLASSIFICATO", name: "[PREMIO]", value: "EUR XXX", condition: "" }] }])} className="flex items-center gap-2 text-sm text-gold"><Plus size={16} /> Aggiungi traguardo premi</button>
        </Card>

        <Card title="Sezione Crescita (FOMO)">
          <Field label="Titolo" value={c.growth.title} onChange={(v) => set(["growth", "title"], v)} />
          <Field label="Testo" value={c.growth.text} onChange={(v) => set(["growth", "text"], v)} textarea />
          <Field label="CTA" value={c.growth.cta} onChange={(v) => set(["growth", "cta"], v)} />
        </Card>

        <Card title="Community & Social">
          <Field label="Titolo" value={c.community.title} onChange={(v) => set(["community", "title"], v)} />
          <Field label="Testo" value={c.community.text} onChange={(v) => set(["community", "text"], v)} textarea />
          <Field label="Link WhatsApp" value={c.community.whatsapp} onChange={(v) => set(["community", "whatsapp"], v)} testid="admin-whatsapp" />
          <Field label="Link Instagram" value={c.community.instagram} onChange={(v) => set(["community", "instagram"], v)} />
          <Field label="Link TikTok" value={c.community.tiktok} onChange={(v) => set(["community", "tiktok"], v)} />
        </Card>

        <Card title="Footer">
          <Field label="Tagline" value={c.footer.tagline} onChange={(v) => set(["footer", "tagline"], v)} />
        </Card>
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("fi_token");
    if (!token) { setChecking(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("fi_token")).finally(() => setChecking(false));
  }, []);

  const logout = () => { localStorage.removeItem("fi_token"); setUser(null); };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-void"><Loader2 className="animate-spin text-imperygreen" /></div>;
  return user ? <Dashboard onLogout={logout} /> : <Login onAuth={setUser} />;
}
